import { Box, Card, Stack, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { bgBlur } from '@/theme/css';
import { useScrollTrigger } from '@/hooks/ui/use-scroll-trigger';
import { ShowIf } from '@/utils';
import { HEADER } from '@/layouts/config-layout';
import { SplashScreen } from '@/components/feedback/loading-screen';
import CustomBreadcrumbs, { BreadcrumbsLinkProps } from '@/components/layout/custom-breadcrumbs';

interface ICustomScreenProps {
  isLoading: boolean;
  heading?: string;
  links: BreadcrumbsLinkProps[];
  action?: React.ReactNode;
  tabs?: React.ReactNode;
  children?: React.ReactNode;
  standalone?: boolean;
  // Spacing customization props
  containerSx?: any; // Custom styles for the main container
}

export default function CustomScreen({
  isLoading,
  containerSx,
  heading,
  links,
  action,
  tabs,
  children,
  standalone,
}: ICustomScreenProps) {
  const theme = useTheme();

  // Hook to detect scroll past 100px for sticky breadcrumbs
  const shouldSticky = useScrollTrigger({
    threshold: 200,
    disableHysteresis: true,
  });

  // Hook to detect when header should hide (same as header component)
  const headerHidden = useScrollTrigger({
    threshold: 200,
    disableHysteresis: false,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: {
          xs: `calc(100vh - ${HEADER.H_MOBILE + 16}px)`,
          lg: `calc(100vh - ${HEADER.H_DESKTOP + 16}px)`,
        },
      }}
    >
      {(heading && heading.trim() !== '') || (links && links.length > 0) ? (
        <CustomBreadcrumbs
          heading={heading}
          links={links}
          action={action}
          sx={{
            pb: { xs: 1, md: 0.5 },
            pt: 0.5,
            mb: { xs: tabs ? 0 : 1, sm: tabs ? 0 : 2, md: tabs ? 0 : 2 },
            px: 0,
            ...(shouldSticky && {
              position: 'sticky',
              top: 0,
              pt: headerHidden ? 1 : `${HEADER.H_DESKTOP_OFFSET + 8}px`,
              pb: 1,
              zIndex: theme.zIndex.appBar - 1,
              ...bgBlur({
                color: theme.palette.background.default,
              }),
              boxShadow: theme.customShadows?.z8 || theme.shadows[8],
              transition: theme.transitions.create(
                [
                  'background-color',
                  'backdrop-filter',
                  'top',
                  'box-shadow',
                  'padding-top',
                  'padding-bottom',
                ],
                {
                  duration: theme.transitions.duration.shorter,
                }
              ),
            }),
          }}
        />
      ) : null}

      {/* Main content wrapper */}
      <Box
        sx={{
          flexGrow: 1,
        }}
      >
        <ShowIf condition={!!standalone}>
          <Container
            maxWidth={false}
            sx={{
              px: { xs: 1, sm: 1.5, md: 2 },
              ...containerSx,
              // Ensure containerSx can override default padding
              ...(containerSx?.px !== undefined && { px: containerSx.px }),
            }}
          >
            <ShowIf condition={isLoading} alternative={children}>
              <Card
                sx={{
                  overflow: 'hidden',
                }}
              >
                <SplashScreen sx={{ bgcolor: 'transparent' }} />
              </Card>
            </ShowIf>
          </Container>
        </ShowIf>
        <ShowIf condition={!standalone}>
          <Container
            maxWidth={false}
            sx={{
              px: { xs: 1, sm: 1.5, md: 2 },
              ...containerSx,
            }}
          >
            {tabs && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderBottom: 1,
                  borderColor: theme.palette.background.neutral,
                  mb: { xs: 2, sm: 3 },
                }}
              >
                {tabs}
              </Stack>
            )}
            <ShowIf condition={isLoading} alternative={children}>
              <Card
                sx={{
                  overflow: 'hidden',
                }}
              >
                <SplashScreen sx={{ bgcolor: 'transparent' }} />
              </Card>
            </ShowIf>
          </Container>
        </ShowIf>
      </Box>

      {/* Spacer to push footer down */}
      <Box sx={{ flexGrow: 1, minHeight: 20 }} />

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: { xs: 1, sm: 1.5, md: 2 },
          mt: 'auto',
          textAlign: 'center',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <Container maxWidth={false}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              opacity: 0.6,
              fontSize: '0.75rem',
              letterSpacing: '0.5px',
            }}
          >
            © {new Date().getFullYear()} Todos los derechos reservados Orión Global
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
